import { FC, useCallback, useMemo } from "react";
import {
  FormControlLabel,
  FormControlLabelProps,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { useAppSelector } from "@/store/hooks";
import { generateDateMetaDataByDates } from "@/components/common/date-range-picker/utils/helpers/generateDateMetaDataByDates";
import { dashboardDatesByDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/filters/dashboardDatesByDashboardIdSelector";
import { currentDashboardIdSelector } from "@/store/dashboards/selectors/dashboard/currentDashboardIdSelector";
import { preDefinedRanges } from "@/components/common/date-range-picker/utils/preDefinedRanges/preDefinedRanges";

interface WidgetSetupDateRangeWithInheritanceSwitcherProps {
  inheritDates: boolean;
  onChange(checked: boolean): void;
}

export const WidgetDateRangeWithInheritanceSwitcher: FC<
  WidgetSetupDateRangeWithInheritanceSwitcherProps
> = ({ onChange, inheritDates }) => {
  const dashboardId = useAppSelector(currentDashboardIdSelector);
  const dashboardDate = useAppSelector((state) =>
    dashboardDatesByDashboardIdSelector(state, dashboardId ?? ""),
  );

  const { tooltip, label } = useMemo(() => {
    if (!dashboardDate) {
      return { tooltip: "", label: "" };
    }

    return generateDateMetaDataByDates({
      dates: dashboardDate,
      ranges: preDefinedRanges,
    });
  }, [dashboardDate]);

  const syncWithDashboardHandler: FormControlLabelProps["onChange"] =
    useCallback(
      (_: unknown, checked: boolean) => {
        onChange(checked);
      },
      [onChange],
    );

  return (
    <FormControlLabel
      sx={{ justifyContent: "space-between" }}
      onChange={syncWithDashboardHandler}
      control={<Switch size="small" checked={inheritDates} />}
      labelPlacement="start"
      label={
        <Stack direction="row" width="100%" spacing={1} alignItems="center">
          <TodayOutlinedIcon />

          <Stack alignItems="start">
            <Typography fontWeight="medium">
              Sync with dashboard date range
            </Typography>
            <Tooltip title={tooltip}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      }
    />
  );
};
